import type { LiveTeamData } from '@api-service/live-team';
import type {
  FplActiveChip,
  FplPosition,
  LiveTeamPlayer
} from '@lib/live/team/types';

/**
 * One fabricated pick. Everything here is either an identifier the live stack
 * joins on, or a label the tile renders.
 */
export type HarnessPick = {
  /** Opta player id. What player channels are keyed by. */
  optaId: string;
  /** FPL element id. What `findPickInState` falls back to joining on. */
  fplId: number;
  displayName: string;
  position: FplPosition;
  teamShortName: string;
  /** Squad slot: 1-11 in the XI, 12-15 on the bench, as FPL numbers them. */
  slot: number;
  /** Fixtures this player features in. Usually one; two for a double. */
  optaMatchIds: string[];
};

export type HarnessSquad = {
  /** Cosmetic here, but carried so the payload matches what /live/team returns. */
  gameweek: number;
  picks: HarnessPick[];
  captainSlot: number;
  viceCaptainSlot: number;
  activeChip: FplActiveChip;
};

export const SQUAD_SIZE = 15;
export const FIRST_BENCH_SLOT = 12;

const XI_SIZE = FIRST_BENCH_SLOT - 1;
const BENCH_BOOST_CHIP = 'bboost';
const TRIPLE_CAPTAIN_CHIP = '3xc';

const STARTER_MULTIPLIER = 1;
const BENCH_MULTIPLIER = 0;
const CAPTAIN_MULTIPLIER = 2;
const TRIPLE_CAPTAIN_MULTIPLIER = 3;

/** FPL's fixed squad shape. Not negotiable, and not derivable from the XI. */
const SQUAD_SHAPE: Record<FplPosition, number> = {
  GK: 2,
  DEF: 5,
  MID: 5,
  FWD: 3
};

/**
 * The same minimums `resolveEffectiveMultipliers` checks when deciding whether
 * a substitution would leave a legal formation. Duplicated deliberately: this
 * validates a squad being *built*, that one validates a swap being *applied*,
 * and coupling them would mean a change to either silently altering the other.
 */
const XI_GOALKEEPER_COUNT = 1;
const XI_MINIMUMS: Record<Exclude<FplPosition, 'GK'>, number> = {
  DEF: 3,
  MID: 2,
  FWD: 1
};

const isStarterSlot = (slot: number): boolean => slot < FIRST_BENCH_SLOT;

const countByPosition = (picks: HarnessPick[]): Record<FplPosition, number> => {
  const counts: Record<FplPosition, number> = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
  for (const pick of picks) counts[pick.position] += 1;
  return counts;
};

const findDuplicates = (values: (string | number)[]): (string | number)[] => {
  const seen = new Set<string | number>();
  const duplicates = new Set<string | number>();
  for (const value of values) {
    if (seen.has(value)) duplicates.add(value);
    seen.add(value);
  }
  return Array.from(duplicates);
};

/**
 * Why a fabricated squad would not behave like a real one.
 *
 * This is not form-polish. `resolveEffectiveMultipliers` assumes a legal FPL
 * squad throughout: it needs exactly one keeper in the XI, the outfield
 * minimums, and a bench keeper for the keeper-only swap rule. An illegal
 * fifteen does not throw, it just quietly stops substituting and stops moving
 * the armband, which presents as a bug in the auto-sub code rather than as a
 * badly built test squad. Refusing the squad up front is what keeps a
 * simulation run from chasing a defect that is not there.
 *
 * @returns every problem found, so the picker can show all of them at once
 * rather than surfacing one per attempt.
 */
export const validateHarnessSquad = (squad: HarnessSquad): string[] => {
  const problems: string[] = [];
  const { picks, captainSlot, viceCaptainSlot } = squad;

  if (picks.length !== SQUAD_SIZE) {
    problems.push(`A squad is ${SQUAD_SIZE} players, not ${picks.length}.`);
  }

  const slots = picks.map((pick) => pick.slot);
  const expectedSlots = Array.from(
    { length: SQUAD_SIZE },
    (_unused, index) => index + 1
  );
  const hasEverySlot =
    picks.length === SQUAD_SIZE &&
    expectedSlots.every((slot) => slots.includes(slot));
  if (!hasEverySlot) {
    problems.push(`Slots must be 1 to ${SQUAD_SIZE}, each used once.`);
  }

  const duplicateOptaIds = findDuplicates(picks.map((pick) => pick.optaId));
  if (duplicateOptaIds.length > 0) {
    // Two picks sharing an Opta id would attach the same player channel twice
    // and double that player's points into the total.
    problems.push(`Repeated player: ${duplicateOptaIds.join(', ')}.`);
  }

  const counts = countByPosition(picks);
  for (const [position, expected] of Object.entries(SQUAD_SHAPE)) {
    const actual = counts[position as FplPosition];
    if (actual !== expected) {
      problems.push(`A squad needs ${expected} ${position}, not ${actual}.`);
    }
  }

  const xi = picks.filter((pick) => isStarterSlot(pick.slot));
  if (xi.length === XI_SIZE) {
    const xiCounts = countByPosition(xi);
    if (xiCounts.GK !== XI_GOALKEEPER_COUNT) {
      problems.push(
        `The XI needs exactly ${XI_GOALKEEPER_COUNT} goalkeeper, not ${xiCounts.GK}.`
      );
    }
    for (const [position, minimum] of Object.entries(XI_MINIMUMS)) {
      const actual = xiCounts[position as FplPosition];
      if (actual < minimum) {
        problems.push(
          `The XI needs at least ${minimum} ${position}, not ${actual}.`
        );
      }
    }
  }

  // FPL takes both armbands from the starting XI. Allowing either on the bench
  // would build a team sheet the scoring rules never have to handle, so
  // whatever the pitch then did with it would prove nothing.
  if (!slots.includes(captainSlot) || !isStarterSlot(captainSlot)) {
    problems.push('The captain must be one of the starting XI.');
  }
  if (!slots.includes(viceCaptainSlot) || !isStarterSlot(viceCaptainSlot)) {
    problems.push('The vice-captain must be one of the starting XI.');
  }
  if (captainSlot === viceCaptainSlot) {
    problems.push('The captain and vice-captain must be different players.');
  }

  return problems;
};

export const isValidHarnessSquad = (squad: HarnessSquad): boolean =>
  validateHarnessSquad(squad).length === 0;

/**
 * The multiplier FPL itself would report for this pick.
 *
 * Mirrored rather than simplified so the fabricated payload is the shape the
 * real `/live/team` returns. `resolveEffectiveMultipliers` re-derives the
 * bench-boost case anyway, but a payload that only worked because a downstream
 * consumer compensated for it would be a poor thing to test against.
 */
const resolveMultiplier = (
  slot: number,
  { captainSlot, activeChip }: HarnessSquad
): number => {
  if (slot === captainSlot) {
    return activeChip === TRIPLE_CAPTAIN_CHIP
      ? TRIPLE_CAPTAIN_MULTIPLIER
      : CAPTAIN_MULTIPLIER;
  }
  if (isStarterSlot(slot)) return STARTER_MULTIPLIER;
  return activeChip === BENCH_BOOST_CHIP
    ? STARTER_MULTIPLIER
    : BENCH_MULTIPLIER;
};

const bySlot = (a: LiveTeamPlayer, b: LiveTeamPlayer) => a.slot - b.slot;

/**
 * Turn a fabricated squad into the payload the team-live provider consumes.
 *
 * Deliberately produces exactly what `GET /live/team` produces, so everything
 * below the provider runs unchanged and untold: channel subscription,
 * availability, auto-subs, the running total, the tiles and the simulated
 * indicator all see an ordinary squad.
 */
export const buildLiveTeamData = (squad: HarnessSquad): LiveTeamData => {
  const toPlayer = (pick: HarnessPick): LiveTeamPlayer =>
    ({
      id: pick.fplId,
      optaId: pick.optaId,
      // Left empty: a squad built from a match roster has no image or kit URL
      // to carry, and PitchPlayer falls back to the blank shirt. Plainer tiles
      // than production, which does not matter for what this is for.
      imageUrl: '',
      kitImageUrl: '',
      displayName: pick.displayName,
      position: pick.position,
      teamShortName: pick.teamShortName,
      slot: pick.slot,
      isStarter: isStarterSlot(pick.slot),
      isCaptain: pick.slot === squad.captainSlot,
      isViceCaptain: pick.slot === squad.viceCaptainSlot,
      multiplier: resolveMultiplier(pick.slot, squad),
      optaMatchIds: pick.optaMatchIds
    } as LiveTeamPlayer);

  const players = squad.picks.map(toPlayer);

  return {
    gameweek: squad.gameweek,
    // The harness always simulates an in-play gameweek: points and multipliers
    // stay provisional so the provider keeps consuming the realtime channels.
    isSettled: false,
    activeChip: squad.activeChip,
    starting: players.filter((player) => player.isStarter).sort(bySlot),
    bench: players.filter((player) => !player.isStarter).sort(bySlot)
  };
};
